import type { FC, FormEvent, KeyboardEvent, ReactNode, RefObject } from "react";

import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Button } from "../common/Button";
import { LoadingSpinner } from "../common/LoadingState";
import {
  type RepositoryChatRetrievalMode,
  type RepositoryChatUiMessage,
  getCitationDataParts,
  renderRepositoryChatMessageText,
} from "./repositoryChatModels";

export const RepositoryChatHeader: FC<{
  repositoryCount: number;
  retrievalMode: RepositoryChatRetrievalMode;
  title: string;
}> = ({ repositoryCount, retrievalMode, title }) => (
  <header className="border-b border-slate-200 bg-slate-50 px-3 py-2.5 sm:px-5 dark:border-slate-700 dark:bg-slate-900/60">
    <div className="flex items-center justify-between gap-2 sm:gap-3">
      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
        <h2 className="truncate text-sm font-semibold text-slate-950 dark:text-slate-50">
          {title}
        </h2>
        <span className="hidden font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-600 md:inline dark:text-emerald-400">
          Live reasoning
        </span>
      </div>

      <div className="flex flex-shrink-0 items-center gap-1.5 sm:gap-2">
        <StatPill
          label="Mode"
          value={retrievalMode.replaceAll("_", " ")}
          tone="emerald"
        />
        <span className="hidden sm:inline-flex">
          <StatPill label="Scope" value={`${repositoryCount} repos`} />
        </span>
      </div>
    </div>
  </header>
);

export const RepositoryChatTranscript: FC<{
  isBusy: boolean;
  messages: RepositoryChatUiMessage[];
  scrollRef: RefObject<HTMLDivElement>;
  taskStatusSlot: ReactNode;
}> = ({ isBusy, messages, scrollRef, taskStatusSlot }) => (
  <div
    ref={scrollRef}
    className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.08),_transparent_28%),linear-gradient(to_bottom,_rgba(15,23,42,0.02),transparent_18rem)] px-3 py-4 sm:space-y-4 sm:px-5 sm:py-5 dark:bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.14),_transparent_28%),linear-gradient(to_bottom,_rgba(2,6,23,0.45),transparent_18rem)]"
  >
    {messages.map((message) => (
      <MessageBubble
        key={message.id}
        isBusy={isBusy}
        isLatest={messages.at(-1)?.id === message.id}
        message={message}
      />
    ))}
    {taskStatusSlot}
  </div>
);

export const RepositoryChatComposer: FC<{
  composerValue: string;
  isBusy: boolean;
  onComposerValueChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
}> = ({ composerValue, isBusy, onComposerValueChange, onSubmit }) => {
  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      if (composerValue.trim() && !isBusy) {
        const form = event.currentTarget.closest("form");
        form?.requestSubmit();
      }
    }
  }

  return (
    <footer className="border-t border-slate-200 bg-white px-3 py-2.5 sm:px-5 sm:py-3 dark:border-slate-700 dark:bg-arti-panel">
      <form className="flex items-end gap-2 sm:gap-3" onSubmit={onSubmit}>
        <textarea
          className="min-h-[2.75rem] max-h-64 flex-1 resize-y overflow-y-auto rounded-none border border-slate-300 bg-slate-50 px-3 py-2.5 text-base text-slate-900 outline-none transition-colors focus:border-emerald-500 sm:text-sm dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
          disabled={isBusy}
          onChange={(event) => onComposerValueChange(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about your repository scope…"
          rows={2}
          value={composerValue}
        />
        <Button
          disabled={isBusy || !composerValue.trim()}
          loading={isBusy}
          loadingText="…"
          type="submit"
        >
          Send
        </Button>
      </form>
      <p className="mt-1.5 text-[10px] text-slate-400 dark:text-slate-500">
        Ctrl+Enter to send
      </p>
    </footer>
  );
};

const StatPill: FC<{
  label: string;
  tone?: "default" | "emerald";
  value: string;
}> = ({ label, tone = "default", value }) => (
  <span
    className={`inline-flex items-center gap-1.5 border px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] ${
      tone === "emerald"
        ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
        : "border-slate-200 bg-white text-slate-600 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300"
    }`}
  >
    <span className="opacity-60">{label}</span>
    <span>{value}</span>
  </span>
);

const MessageBubble: FC<{
  isBusy: boolean;
  isLatest: boolean;
  message: RepositoryChatUiMessage;
}> = ({ isBusy, isLatest, message }) => {
  const markdown = renderRepositoryChatMessageText(message);

  return (
    <article
      className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
    >
      <div
        className={`w-full border px-3 py-2.5 shadow-sm sm:w-auto sm:max-w-4xl sm:px-4 sm:py-3 ${
          message.role === "user"
            ? "border-emerald-500 bg-emerald-500 text-slate-950"
            : "border-slate-200 bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
        }`}
      >
        <div className="mb-2 flex items-center justify-between gap-4">
          <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em]">
            {message.role === "user" ? "You" : "Arti"}
          </span>
          {message.role === "assistant" && isBusy && isLatest ? (
            <LoadingSpinner size="sm" />
          ) : null}
        </div>

        <MessageMarkdown isUser={message.role === "user"} markdown={markdown} />

        {message.role === "assistant" ? (
          <CitationPanels parts={message.parts} />
        ) : null}
      </div>
    </article>
  );
};

const MessageMarkdown: FC<{ isUser: boolean; markdown: string }> = ({
  isUser,
  markdown,
}) => (
  <article
    className={`markdown-content repository-chat-markdown ${
      isUser
        ? "repository-chat-markdown--user"
        : "repository-chat-markdown--assistant"
    }`}
  >
    <Markdown remarkPlugins={[remarkGfm]}>{markdown}</Markdown>
  </article>
);

const CitationPanels: FC<{ parts: RepositoryChatUiMessage["parts"] }> = ({
  parts,
}) => {
  const citationData = getCitationDataParts(parts);
  if (citationData.length === 0) {
    return null;
  }

  return (
    <div className="mt-4 space-y-3">
      {citationData.map((data) => (
        <div
          key={`${data.turnId}-${data.citations.length}`}
          className="border border-slate-200 bg-slate-50 px-3 py-3 dark:border-slate-700 dark:bg-slate-900/70"
        >
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-600 dark:text-emerald-400">
              Evidence set
            </span>
            {data.consultedRepos.map((repositorySlug) => (
              <span
                key={repositorySlug}
                className="border border-slate-300 px-2 py-1 font-mono text-[10px] font-semibold text-slate-600 dark:border-slate-700 dark:text-slate-300"
              >
                {repositorySlug}
              </span>
            ))}
          </div>

          <div className="space-y-2">
            {data.citations.map((citation) => (
              <div
                key={citation.citationId}
                className="border border-slate-200 bg-white px-3 py-3 dark:border-slate-800 dark:bg-slate-950"
              >
                <div className="break-all font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-slate-700 dark:text-slate-200">
                  {citation.repositorySlug} · {citation.path}:
                  {citation.startLine}-{citation.endLine}
                </div>
                {citation.summary ? (
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                    {citation.summary}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
